import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { DateInput } from '../../../components/ui/date-input';
import { Textarea } from '../../../components/ui/textarea';
import { SaveChangesButton, CancelButton, CloseButton } from '@/styles/StandardButtons';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { colors } from '@/design-system/colors';
import "@/design-system/typography.css";
import '@/design-system/forms.css';
import AddressAutocomplete, { AddressSelection } from './AddressAutocomplete';
import { ClientLeadCombobox as ClientCombobox, ClientLeadSelection as ClientSelection } from '@/components/shared/ClientLeadCombobox';
import { ClientForm } from '@/features/people/clients/components/ClientForm';
import { createClientUser } from '@/features/people/clients/store';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { uploadProjectAttachments, deleteProjectAttachment, type ProjectFileAttachment } from '../store';
import { cn } from '@/lib/utils';
import countries from '@/lib/countries';
import { PROJECT_TYPE_OPTIONS } from '@/lib/project-types';
import { fetchTeamMemberLookup } from '@/lib/entity-api';
import { useUser } from '@/contexts/UserContext';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { ProjectMembersTab } from './ProjectMembersTab';
import { useProjectMembers } from './useProjectMembers';
import { NO_INTERNAL_MEMBER_MESSAGE } from './projectAccessApi';
import { useVisibleTabs } from '@/hooks/useVisibleTabs';

const VENDOR_CANNOT_CREATE_PROJECT = 'You cannot create new projects with this account type.';
const FORM_TABS = [{ value: 'info', label: 'Project Info' }, { value: 'members', label: 'Members' }] as const;
const FORM_TAB_KEYS = { info: 'projects.tab.info', members: 'projects.tab.members' } as const;

// Root cause of the "Maximum update depth exceeded" loop (2026-09-24):
// `attachments = []` as an inline default parameter creates a BRAND NEW
// array on every render whenever a caller (NewProjectButton.tsx) doesn't
// pass the prop at all. That new reference fed the useEffect below's
// dependency array, so the effect re-ran every render, called setState,
// triggered a re-render, got a new default `[]` again, and looped forever.
// A single stable module-level reference fixes it without losing the
// "no attachments passed" behavior.
const EMPTY_ATTACHMENTS: ProjectFileAttachment[] = [];

interface Project {
  id: string;
  project_id?: string | null;
  name: string;
  client_name: string;
  client_id: string | null;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  latitude: number | null;
  longitude: number | null;
  start_date: string;
  end_date: string;
  budget: number;
  description: string;
  status: string;
  project_type: string;
  square_footage: number;
  assignee_id: string | null;
  created_at: string;
  company_id: string;
  created_by: string;
}

export interface ProjectFormData {
  name: string;
  client_name: string;
  client_id: string | null;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  latitude: number | null;
  longitude: number | null;
  start_date: string;
  end_date: string;
  budget: number;
  description: string;
  status: string;
  project_type: string;
  square_footage: number;
  assignee_id: string | null;
  attachmentsFiles?: File[];
  /** Edit only: the final list of local ids when the Members tab changed it, otherwise undefined */
  members?: string[];
  /** true when the saver is not an admin: `members` are people to add, not the final list */
}

const createFormDataFromProject = (project?: Project | null): ProjectFormData => ({
  name: project?.name || '',
  client_name: project?.client_name || '',
  client_id: project?.client_id || null,
  address: project?.address || '',
  city: project?.city || '',
  state: project?.state || '',
  country: project?.country || '',
  zip_code: project?.zip_code || '',
  latitude: project?.latitude ?? null,
  longitude: project?.longitude ?? null,
  start_date: project?.start_date || '',
  end_date: project?.end_date || '',
  budget: project?.budget || 0,
  description: project?.description || '',
  status: project?.status || '',
  project_type: project?.project_type || '',
  square_footage: project?.square_footage || 0,
  assignee_id: project?.assignee_id || null
});

interface ProjectFormProps {
  mode: 'create' | 'edit';
  initialData?: Project | null;
  onSubmit: (data: ProjectFormData) => void | Promise<void>;
  onCancel: () => void;
  isOpen: boolean;
  error?: string | null;
  /** Existing attachments to show when editing. Ignored in create mode. */
  attachments?: ProjectFileAttachment[];
  /** Called after a delete succeeds, so the parent can keep its own list (if any) in sync. */
  onDeleteAttachment?: (attachmentId: string) => void;
}

// Use the same input styles as GenericForm
const inputStyles = "font-form-input generic-form-field";

export function ProjectForm({ mode, initialData, onSubmit, onCancel, isOpen, error, attachments = EMPTY_ATTACHMENTS, onDeleteAttachment }: ProjectFormProps) {
  const { companyId, user, userRole } = useUser();
  const { ensureMembers, confirmAddingPeople, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const uploadProjectAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<ProjectFileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadProjectAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteProjectAttachmentFn = useCallback((id: string) => deleteProjectAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFileBase,
    resetPending: resetSelectedFiles,
  } = useAttachmentStaging<ProjectFileAttachment>({
    entityId: initialData?.id,
    uploadFn: uploadProjectAttachmentsFn,
    deleteFn: deleteProjectAttachmentFn,
    initialUploadedFiles: attachments,
  });
  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    const ok = await handleDeleteFileBase(attachmentId);
    if (ok) {
      onDeleteAttachment?.(attachmentId);
    }
  }, [handleDeleteFileBase, onDeleteAttachment]);
  const [formData, setFormData] = useState<Record<string, any>>(createFormDataFromProject(initialData));
  const [clientId, setClientId] = useState<string | null>(initialData?.client_id || null);
  const [latitude, setLatitude] = useState<number | null>(initialData?.latitude ?? null);
  const [longitude, setLongitude] = useState<number | null>(initialData?.longitude ?? null);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; user_id: string; first_name: string; last_name: string; email: string }>>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [clientListRefreshToken, setClientListRefreshToken] = useState(0);
  const [activeTab, setActiveTab] = useState<'info' | 'members'>('info');
  const editingId = mode === 'edit' ? initialData?.id : null;
  const isCreate = mode === 'create';
  const members = useProjectMembers(editingId, isOpen, { forNewProject: isCreate });
  // Admins and members of the project manage the whole list alike. The tab also shows while loading and when the load failed so that neither is silent.
  // A new project has the tab too (an empty draft: the creator is added by the server).
  // Each tab is also an element of the navigation matrix: a tab that is OFF for this role is neither offered nor mounted
  const formTabs = useVisibleTabs(FORM_TABS, FORM_TAB_KEYS);
  const showMembersTab = formTabs.has('members') && (isCreate || (!!editingId && (members.canManage || members.loading || !!members.loadError)));
  const currentTab = (showMembersTab ? formTabs.activeOf(activeTab) : undefined) ?? 'info';

  useEffect(() => {
    const initial = createFormDataFromProject(initialData);
    setFormData(initial);
    setClientId(initialData?.client_id || null);
    setLatitude(initialData?.latitude ?? null);
    setLongitude(initialData?.longitude ?? null);
    // Clear validation error when form data changes
    setValidationError(null);
    // Reset attachment state for the new project/create session -- pending
    // files never got uploaded (create mode with no id yet), so there's
    // nothing to clean up server-side; they simply never existed anywhere
    // but this component's own state.
    resetSelectedFiles();
    setUploadedFiles(attachments);
  }, [initialData, attachments, resetSelectedFiles, setUploadedFiles]);

  // Fetch team members for assignee dropdown
  useEffect(() => {
    if (!companyId) return;

    const fetchTeamMembers = async () => {
      try {
        // The company's active admins and members (generic lookup, by the API), by first name
        const team = await fetchTeamMemberLookup(['member', 'admin']);
        setTeamMembers(
          team
            .map((user) => ({ id: user.id, user_id: user.user_id, first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '' }))
            .sort((x, y) => x.first_name.localeCompare(y.first_name))
        );
      } catch (error) {
        console.error('Failed to fetch team members:', error);
      }
    };

    fetchTeamMembers();
  }, [companyId]);

  const projectFields = useMemo<GenericFormField[]>(() => [
    { name: 'name', label: 'Project Name', type: 'text', required: true, row: 1, placeholder: 'Enter project name' },
    { name: 'city', label: 'City', type: 'text', required: true, row: 3, colSpan: 0.5, placeholder: 'Enter city' },
    { name: 'state', label: 'State', type: 'text', required: true, row: 3, colSpan: 0.5, placeholder: 'Enter state' },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      required: false,
      row: 3,
      colSpan: 0.5,
      options: countries.map(c => ({ label: c, value: c })),
      placeholder: 'Select',
    },
    { name: 'zip_code', label: 'ZIP Code', type: 'text', required: true, row: 3, colSpan: 0.5, placeholder: 'Enter ZIP code' },
    { name: 'start_date', label: 'Start Date', type: 'date', required: false, row: 4, colSpan: 0.5 },
    { name: 'end_date', label: 'End Date', type: 'date', required: false, row: 4, colSpan: 0.5 },
    { name: 'budget', label: 'Budget', type: 'number', required: false, row: 4, colSpan: 0.5, placeholder: '0.00' },
    { name: 'square_footage', label: 'Square Footage', type: 'number', required: false, row: 4, colSpan: 0.5, placeholder: '0' },
    {
      name: 'project_type',
      label: 'Project Type',
      type: 'select',
      required: false,
      row: 5,
      colSpan: 0.5,
      options: PROJECT_TYPE_OPTIONS,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: false,
      row: 5,
      colSpan: 0.5,
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Completed', value: 'completed' },
        { label: 'On Hold', value: 'on_hold' },
        { label: 'Cancelled', value: 'cancelled' },
      ]
    },
    {
      name: 'assignee_id',
      label: 'Assignee',
      type: 'select',
      required: false,
      row: 5,
      colSpan: 0.5,
      options: teamMembers.map(member => ({
        label: `${member.first_name} ${member.last_name}`.trim() || member.email,
        value: member.id
      })),
      placeholder: 'Select'
    },
    { name: 'description', label: 'Description', type: 'textarea', row: 6, colSpan: 2, placeholder: 'Enter project scope here as detailed as possible' },
  ], [teamMembers]);

  const handleFieldChange = (name: string, value: any, updatedFormData: Record<string, any>) => {
    setFormData(updatedFormData as ProjectFormData);
  };

  const handleSubmit = async (data: Record<string, any>) => {
    // Clear previous validation errors
    setValidationError(null);

    // A vendor can look at this form but cannot create a project: say so at the top and do not send anything (the server refuses it too)
    if (isCreate && userRole === 'vendor') {
      setValidationError(VENDOR_CANNOT_CREATE_PROJECT);
      return;
    }

    // Validate required fields
    const requiredFields = [
      { key: 'name', label: 'Project Name' },
      { key: 'client_name', label: 'Client Name' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'country', label: 'Country' },
      { key: 'zip_code', label: 'ZIP Code' },
    ];

    const missingFields: string[] = [];
    
    for (const field of requiredFields) {
      const value = field.key === 'client_name' ? formData.client_name : data[field.key];
      if (!value || (typeof value === 'string' && !value.trim())) {
        missingFields.push(field.label);
      }
    }

    if (missingFields.length > 0) {
      setValidationError('Please fill in all required fields before saving.');
      return; // Don't submit if validation fails
    }

    // A project always keeps an internal member; the server refuses the same list, this just keeps the modal open on the right tab
    if (showMembersTab && members.dirty && members.internal.length === 0) {
      setActiveTab('members');
      setValidationError(NO_INTERNAL_MEMBER_MESSAGE);
      return;
    }

    // Now-optional fields: an empty date/number/select input submits '' from
    // the DOM, which Postgres rejects for `date`/`numeric` columns (e.g.
    // "invalid input syntax for type date: \"\""). Send null instead so
    // leaving one of these blank actually saves as empty, not a 400.
    const nullableIfEmpty = ['start_date', 'end_date', 'budget', 'square_footage', 'project_type', 'status', 'assignee_id'];
    const sanitized = { ...data };
    for (const key of nullableIfEmpty) {
      if (sanitized[key] === '') {
        sanitized[key] = null;
      }
    }

    // Everybody chosen for this project -- the client, the assignee and the members of the Members tab -- must belong to it. Whoever is not a member yet is asked ONCE for all of them
    // (cancelling saves nothing); confirming makes them members as part of this same save.
    // Admins and members of the project can add anybody (internal, client or vendor): nobody chosen is left out of the project.
    const selfId = teamMembers.find((member) => member.user_id === user?.id)?.id;
    let finalMembers: string[] | undefined = showMembersTab && members.dirty ? members.ids : undefined;
    if (showMembersTab && members.loaded) {
      const chosen = Array.from(new Set([...members.ids, clientId, sanitized.assignee_id].filter((id): id is string => !!id)));
      const alreadyThere = new Set<string>([...members.storedIds, ...(isCreate && selfId ? [selfId] : [])]);
      const pending = chosen.filter((id) => !alreadyThere.has(id));
      if (pending.length > 0) {
        if (!(await confirmAddingPeople(true))) return;
        finalMembers = chosen;
      }
    } else if (editingId) {
      // the members could not be loaded: the client / assignee that changed are checked one by one
      const changed = [clientId !== (initialData?.client_id || null) ? clientId : null, sanitized.assignee_id !== (initialData?.assignee_id || null) ? sanitized.assignee_id : null];
      if (!(await ensureMembers(editingId, changed.map((id) => (id ? { id, name: 'The selected user' } : null))))) return;
    }

    // If validation passes, proceed with submission
    onSubmit({
      ...sanitized,
      client_id: clientId,
      client_name: formData.client_name,
      address: formData.address,
      latitude,
      longitude,
      attachmentsFiles: selectedFiles,
      members: finalMembers,
    } as ProjectFormData);
  };

  const handleClientChange = (selection: ClientSelection) => {
    setClientId(selection.client_id);
    setFormData(prev => ({
      ...prev,
      client_name: selection.client_name
    }));
  };

  const handleSaveNewClient = async (clientFormData: any) => {
    if (!companyId || !user?.id) return;
    const created = await createClientUser(companyId, user.id, clientFormData);
    handleClientChange({ client_id: created.id, client_name: created.name });
    // Force ClientCombobox to refetch so the new client shows up in its
    // dropdown immediately -- it otherwise only fetches once on mount.
    setClientListRefreshToken((prev) => prev + 1);
    setIsAddClientOpen(false);
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
    setLatitude(null);
    setLongitude(null);
  };

  // Helper function to match country from API to our countries list
  const matchCountry = (apiCountry: string | undefined): string | undefined => {
    if (!apiCountry) return undefined;
    
    // Try exact match first
    const exactMatch = countries.find(c => c.toLowerCase() === apiCountry.toLowerCase());
    if (exactMatch) return exactMatch;
    
    // Try common variations
    const countryVariations: Record<string, string> = {
      'us': 'United States',
      'usa': 'United States',
      'united states of america': 'United States',
      'uk': 'United Kingdom',
      'united kingdom of great britain and northern ireland': 'United Kingdom',
      'uae': 'United Arab Emirates',
      'south korea': 'South Korea',
      'north korea': 'North Korea',
      'russia': 'Russia',
      'russian federation': 'Russia',
    };
    
    const normalized = apiCountry.toLowerCase().trim();
    if (countryVariations[normalized]) {
      return countryVariations[normalized];
    }
    
    // Try partial match (e.g., "United States" matches "United States of America")
    const partialMatch = countries.find(c => 
      c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    return undefined;
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    const matchedCountry = matchCountry(selection.country);
    setFormData(prev => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      ...(matchedCountry && { country: matchedCountry }),
    }));
    setLatitude(selection.latitude ?? null);
    setLongitude(selection.longitude ?? null);
  };

  // Helper function for unsaved changes detection
  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    const currentKeys = Object.keys(current);
    const initialKeys = Object.keys(initial);
    const allKeysSet = new Set<string>();
    currentKeys.forEach(key => allKeysSet.add(key));
    initialKeys.forEach(key => allKeysSet.add(key));
    const allKeys = Array.from(allKeysSet);
    
    for (const key of allKeys) {
      const currentValue = current[key];
      const initialValue = initial[key];
      
      // Normalize values: treat null, undefined, empty string, and 0 as equivalent for comparison
      const normalizeValue = (val: any): string => {
        if (val == null) return '';
        if (val === '') return '';
        if (typeof val === 'number' && val === 0) return '0';
        return String(val).trim();
      };
      
      const currentStr = normalizeValue(currentValue);
      const initialStr = normalizeValue(initialValue);
      
      if (currentStr !== initialStr) {
        return true;
      }
    }
    return false;
  };

  const initialValuesRef = React.useRef<Record<string, any>>({});
  const previousIsOpenRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      // Capture initial values from initialData when form first opens
      const initialValues = createFormDataFromProject(initialData);
      const deepCopy = JSON.parse(JSON.stringify(initialValues));
      initialValuesRef.current = deepCopy;
      // New Project reset bug fix (2026-09-18): this is the effect that
      // actually detects a closed->open transition (the other reset
      // effect above only re-runs when the `initialData` PROP itself
      // changes, which never happens between two consecutive "New
      // Project" opens -- it's null both times -- so a previous
      // session's typed-in formData used to survive into the next
      // "New Project" open). Reset the same pieces of state here too,
      // on every open, for both create (blank) and edit (initialData's
      // real values) alike.
      setFormData(deepCopy);
      setClientId(initialData?.client_id || null);
      setLatitude(initialData?.latitude ?? null);
      setLongitude(initialData?.longitude ?? null);
      // Clear validation errors when form opens
      setValidationError(null);
      setActiveTab('info');
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, initialData, mode]);

  // Render field helper function
  const renderField = (field: GenericFormField) => {
    if (field.type === 'select') {
      return (
        <GenericSelect
          value={formData[field.name] || ''}
          onValueChange={(value) => {
            handleFieldChange(field.name, value, { ...formData, [field.name]: value });
          }}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          contentClassName={field.name === 'country' ? "max-h-[18.75rem] overflow-y-auto" : ""}
          options={field.options || []}
        />
      );
    } else if (field.type === 'textarea') {
      return (
        <Textarea
          id={field.name}
          value={formData[field.name] || ''}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );
    } else if (field.type === 'date') {
      return (
        <DateInput
          id={field.name}
          value={formData[field.name] || ''}
          onChange={(iso) => handleFieldChange(field.name, iso, { ...formData, [field.name]: iso })}
          placeholder={field.placeholder}
          className={inputStyles}
          disabled={field.disabled}
        />
      );
    } else if (field.type === 'number' && field.name === 'budget') {
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-sm">$</span>
          <Input
            id={field.name}
            type="number"
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, parseFloat(e.target.value) || 0, { ...formData, [field.name]: parseFloat(e.target.value) || 0 })}
            placeholder={field.placeholder}
            className={`${inputStyles} pl-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            disabled={field.disabled}
            onKeyDown={(e) => {
              if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onFocus={(e) => {
              e.currentTarget.addEventListener('wheel', (event) => {
                event.preventDefault();
              }, { passive: false });
            }}
            style={{
              MozAppearance: 'textfield',
              WebkitAppearance: 'none',
              textIndent: '0.7rem',
            }}
          />
        </div>
      );
    } else {
      return (
        <Input
          id={field.name}
          type={field.type}
          value={formData[field.name] || ''}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          className={field.type === 'number' 
            ? `${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`
            : inputStyles
          }
          disabled={field.disabled}
          onKeyDown={(e) => {
            if (field.type === 'number' && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => {
            if (field.type === 'number') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onFocus={(e) => {
            if (field.type === 'number') {
              e.currentTarget.addEventListener('wheel', (event) => {
                event.preventDefault();
              }, { passive: false });
            }
          }}
        />
      );
    }
  };

  // Group fields by row
  const fieldsByRow = projectFields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  // Custom layout that includes ClientCombobox and AddressAutocomplete
  const customLayout = (
    <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(formData);
        }}
        className={cn("generic-form-container", "w-[80vw]")}
        style={{ maxHeight: 'var(--form-max-height-default)', height: showMembersTab ? 'var(--form-max-height-default)' : undefined }} // with tabs the modal keeps one size, so it does not jump under the pointer when the tab changes
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header - using GenericForm classes and colors */}
      <div 
        className={cn("generic-form-header", "generic-form-header-default")}
        style={{ 
          backgroundColor: colors.formHeaderFooter,
          borderColor: colors.tableBorder
        }}
      >
        <h2 className="font-form-title">
          {mode === 'create' ? 'Create Project' : 'Edit Project'}
        </h2>
        <CloseButton
          onClick={() => {
            const isActuallyDirty = hasFormChanged(formData, initialValuesRef.current) || (showMembersTab && members.dirty);
            if (isActuallyDirty) {
              if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
                onCancel();
              }
            } else {
              onCancel();
            }
          }}
        />
      </div>

      {showMembersTab && formTabs.tabs.length > 1 && (
        <div className="px-6 pt-4">
          <GenericTabBar tabs={[...formTabs.tabs]} activeTab={currentTab} onTabChange={(tab) => setActiveTab(tab as 'info' | 'members')} />
        </div>
      )}

      {/* Body - using GenericForm classes */}
      <div className={cn("generic-form-body", "generic-form-body-default")}>
        {(error || validationError) && (
          <div className="generic-form-error">
            {validationError || error}
          </div>
        )}
        {membershipPrompt /* outside the tabs: the question can come up from either one */}
        {showMembersTab && currentTab === 'members' && <ProjectMembersTab projectId={editingId || 'new'} members={members} />}
        {/* Each tab shows only its own content; what is typed in Project Info lives in formData, so switching tabs loses nothing */}
        {!(showMembersTab && currentTab === 'members') && (
        <div className="generic-form-body-spacing-default">
          {/* Row 1: Project Name + Client Name */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            <div className="space-y-2">
              <Label htmlFor="name" className="font-form-label">
                Project Name <span className="text-red-500">*</span>
              </Label>
              {renderField(projectFields.find(f => f.name === 'name')!)}
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_name" className="font-form-label">
                Client Name <span className="text-red-500">*</span>
              </Label>
              <ClientCombobox
                value={formData.client_name}
                onChange={handleClientChange}
                clearable={false}
                placeholder="Select or type client name"
                onCreateNew={() => setIsAddClientOpen(true)}
                refreshToken={clientListRefreshToken}
              />
            </div>
          </div>

          {/* Row 2: Address (full width) */}
          <div className="space-y-2">
            <Label htmlFor="address" className="font-form-label">
              Address <span className="text-red-500">*</span>
            </Label>
            <AddressAutocomplete
              value={formData.address}
              onChange={handleAddressChange}
              onSelectSuggestion={handleAddressSelect}
              placeholder="Start typing address"
              inputClassName="w-full"
              initialSelected={Boolean(initialData)}
            />
          </div>

          {/* Row 3: City, State, Country, ZIP Code (each 1/4 width) */}
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[3]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Remaining rows (excluding row 1 and row 3) */}
          {sortedRows.filter(rowKey => Number(rowKey) !== 1 && Number(rowKey) !== 3).map((rowKey) => {
            const rowFields = fieldsByRow[Number(rowKey)];
            const hasQuarterWidthFields = rowFields.some(f => f.colSpan === 0.5);
            
            return (
              <div
                key={rowKey}
                className={hasQuarterWidthFields ? "grid grid-cols-1 md:grid-cols-4" : "grid grid-cols-1 md:grid-cols-2"}
                style={{ gap: 'var(--field-gap-default)' }}
              >
                {rowFields.map((field) => (
                  <div
                    key={field.name}
                    className={cn(
                      "space-y-2",
                      field.colSpan === 2 ? "md:col-span-2" : 
                      field.colSpan === 0.5 ? "md:col-span-1" : 
                      "md:col-span-1"
                    )}
                  >
                    <Label htmlFor={field.name} className="font-form-label">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Attachments -- available in both create and edit mode. In
              create mode, files are held in `selectedFiles` and uploaded by
              the caller only after the project is actually created (see
              `attachmentsFiles` in the onSubmit payload above); this is the
              exact same pattern Create Lead uses. */}
          <div className="space-y-2">
            <GenericAttachmentsSection
              attachments={uploadedFiles.map(file => ({
                id: file.id,
                file_name: file.file_name,
                file_type: file.file_type,
                file_size: file.file_size,
                file_url: file.file_url,
                created_at: file.created_at,
                created_by: file.created_by,
                created_by_user: file.created_by_user
              }))}
              pendingFiles={selectedFiles}
              onUpload={handleFileUpload}
              onDelete={handleDeleteFile}
              onRemovePending={handleRemoveSelectedFile}
              mode={mode}
              // a project's own Drive: only an existing project has one (a new project has nothing to browse yet)
              projectId={mode === 'edit' && initialData?.id ? initialData.id : null}
            />
          </div>
        </div>
        )}
      </div>

      {/* Footer - using GenericForm classes and colors */}
      <div 
        className={cn("generic-form-footer", "generic-form-footer-default")}
        style={{ 
          backgroundColor: colors.formHeaderFooter,
          borderColor: colors.tableBorder
        }}
      >
        <CancelButton
          onClick={() => {
            const isActuallyDirty = hasFormChanged(formData, initialValuesRef.current) || (showMembersTab && members.dirty);
            if (isActuallyDirty) {
              if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
                onCancel();
              }
            } else {
              onCancel();
            }
          }}
        />
        <SaveChangesButton />
      </div>
    </form>
  );

  return (
    <>
      <GenericForm
        title="Project"
        mode={mode}
        fields={[]} // Empty since we're using customLayout
        initialValues={formData}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isOpen={isOpen}
        error={error}
        submitButtonText="Save Changes"
        customLayout={customLayout}
        onFieldChange={handleFieldChange}
        hasUnsavedChanges={() => hasFormChanged(formData, initialValuesRef.current) || (showMembersTab && members.dirty)}
      />
      {/* Stacked on top of the project form -- does not close it -- so
          picking "Add new client" mid-flow doesn't lose project edits. */}
      <ClientForm
        isOpen={isAddClientOpen}
        client={null}
        onClose={() => setIsAddClientOpen(false)}
        onSave={handleSaveNewClient}
      />
    </>
  );
}

